import * as React from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { View, Text } from 'react-native';
import { Icon } from 'react-native-elements';
import useNotProcessedOrders from 'hooks/useNotProcessedOrders';
import { getOrders } from 'services/ordersService';
import { styles } from 'styles/screens/visualizationScreen';
import OrderContext from 'contexts/OrderContext';
import Order from 'types/models/Order';
import OrderCard from 'components/OrderCard';
import OrdersScreenProps from 'types/screens/VisualizationScreenProps';
import UserRoleType from 'types/enums/UserRoleType';
import { useTranslation } from 'react-i18next';
import AppContext from 'contexts/AppContext';
import { useWindowDimensions } from 'react-native';

export default ({ navigation }: OrdersScreenProps): JSX.Element => {
  const { processPendingOrders } = useNotProcessedOrders();
  const [orders, setOrders] = React.useState<Order[]>([]);
  const { currentCommerce, isConnected, currentVenue } = React.useContext(OrderContext);
  const { authData } = React.useContext(AppContext);

  let currentInterval: NodeJS.Timer;
  const isCommerceEditor = authData?.user.role === UserRoleType.CommerceEditor;

  const { t } = useTranslation();

  const { width } = useWindowDimensions();

  const fetchOrders = async () => {
    if (!currentCommerce || !currentVenue) return;
    try {
      const orders = await getOrders(currentCommerce.id, currentVenue.id, {billingRange: currentVenue.billingRange ?? 12});
      setOrders(orders);
    } catch (error) {
      console.log(error);
      setOrders([]);
    }
  };

  const startOrdersInterval = async (): Promise<NodeJS.Timer> => {
    if (!currentInterval) {
      await fetchOrders();
    }
    return setInterval(() => fetchOrders(), 5000);
  };

  useFocusEffect(
    React.useCallback(() => {
      return () => {
        clearInterval(currentInterval);
      };
    }, []),
  );

  useFocusEffect(
    React.useCallback(() => {
      if (currentCommerce) {
        startOrdersInterval().then((interval) => {
          currentInterval = interval;
        });
      } else {
        setTimeout(() => {
          //ver si es editor de commerce
          navigation.reset({
            index: 1,
            routes: [isCommerceEditor ? { name: 'OrdersScreen' } : { name: 'CommercesScreen' }],
          });
        }, 500);
      }
      return () => clearInterval(currentInterval);
    }, []),
  );

  React.useEffect(() => {
    const uploadingPendingOrders = async () => {
      await processPendingOrders();
    };
    uploadingPendingOrders();
  }, [isConnected]);

  const isLargeScreen = width >= 1700;
  const isSmallScreen = width <= 1160;

  return (
    <View style={styles.container}>
      <View style={[styles.posterLarge, { backgroundColor: `${currentVenue?.appColor}CC` ?? '#FFFFFF' }]}>
        <Icon
          tvParallaxProperties={null}
          name="arrow-back-outline"
          type="ionicon"
          color="#FFFFFF"
          size={40}
          containerStyle={styles.goBackContainerLarge}
          onPress={() => navigation.goBack()}
        />
        <View style={isLargeScreen ? styles.textPosterLargeContainer : styles.textPosterMediumContainer}>
          <Text
            style={
              isLargeScreen ? styles.textPosterLarge 
              : isSmallScreen ? styles.textPosterSmall 
              : styles.textPosterMedium
            }
          >
          {currentCommerce?.name}
          </Text>
        </View>
        
      </View>
      <View style={ isLargeScreen ? styles.cardsContainerLarge : styles.cardsContainerMedium}>
        <View style={ isLargeScreen ? styles.cardColumnLarge : styles.cardColumnMedium}>
          <View style={[ isLargeScreen ? styles.cardHeaderLarge : styles.cardHeaderMedium, { backgroundColor: '#D70B0B' }]}>
            <Text style={[ isLargeScreen ? styles.textLarge : isSmallScreen ? styles.textSmall : styles.textMedium]}>{t('pendings')}</Text>
          </View>
          {orders.length > 0 &&
            orders
              .filter((order) => order.status === 2 || order.status === 3)
              .map((order: any, index) => {
                return <OrderCard key={index} order={order} forVisualization={true} />;
              })}
        </View>
        <View style={ isLargeScreen ? styles.cardColumnLarge : styles.cardColumnMedium}>
          <View style={[isLargeScreen ? styles.cardHeaderLarge : styles.cardHeaderMedium, { backgroundColor: '#FF8A00' }]}>
            <Text style={[isLargeScreen ? styles.textLarge : isSmallScreen ? styles.textSmall : styles.textMedium]}>{t('in-proccess')}</Text>
          </View>
          {orders.length > 0 &&
            orders
              .filter((order) => order.status === 4)
              .map((order: any, index) => {
                return <OrderCard key={index} order={order} forVisualization={true} />;
              })}
        </View>
        <View style={ isLargeScreen ? styles.cardColumnLarge : styles.cardColumnMedium}>
          <View style={[isLargeScreen ? styles.cardHeaderLarge : styles.cardHeaderMedium, { backgroundColor: '#0D9E00' }]}>
            <Text style={[isLargeScreen ? styles.textLarge : isSmallScreen ? styles.textSmall : styles.textMedium]}>{t('to-deliver')}</Text>
          </View>
          {orders.length > 0 &&
            orders
              .filter((order) => order.status === 5)
              .map((order: any, index) => {
                return <OrderCard key={index} order={order} forVisualization={true} />;
              })}
        </View>
      </View>
    </View>
  );
};
